import React, { forwardRef } from 'react';
import cn from 'classnames';
import { Calendar } from 'lucide-react';
import {
  useDateInput,
  UseDateInputOptions
} from '../../../utils/types/use-date-input';
import { useThemeContext } from '../../../utils/providers/theme-context';

type DateInputProps = UseDateInputOptions & {
  id?: string;
  name?: string;
  label?: string | React.ReactNode;
  placeholder?: string;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  wrapperClass?: string;
  labelClass?: string;
  disabled?: boolean;
  withShortcuts?: boolean;
};

const sizeToClasses = (size: DateInputProps['size']) =>
  size === 'small' ? 'h-9' : size === 'large' ? 'h-12' : 'h-10';

const DateInput = forwardRef<HTMLInputElement, DateInputProps>((props, ref) => {
  const {
    id,
    name,
    label,
    placeholder,
    size = 'medium',
    className,
    wrapperClass,
    labelClass,
    disabled,
    withShortcuts = true,
    ...hookOpts
  } = props;

  const { theme, getColour } = useThemeContext();
  const {
    ref: internalRef,
    value,
    min,
    max,
    error,
    setToday,
    handleBlur,
    handleKeyDown,
    setValue
  } = useDateInput(hookOpts);

  const inputClass = cn(
    // layout
    'block w-full min-w-0 rounded-2xl pl-10 pr-3 border transition-colors appearance-none',
    // mobile typography: keep 16px to avoid iOS zoom; reduce on sm+
    'text-[16px] sm:text-sm',
    sizeToClasses(size),
    // focus
    'focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500',
    // theme: light
    {
      'bg-white shadow-sm': theme === 'light',
      'border-zinc-950/10 hover:border-zinc-950/20 text-zinc-950 placeholder:text-zinc-500':
        theme === 'light',
      'disabled:bg-zinc-950/5 disabled:shadow-none': theme === 'light'
    },
    // theme: dark
    {
      'bg-zinc-950': theme === 'dark',
      'border-white/10 hover:border-white/20 text-white placeholder:text-zinc-400':
        theme === 'dark',
      'disabled:bg-white/5': theme === 'dark'
    },
    'disabled:opacity-50',
    className
  );

  const openPicker = () => {
    const el = internalRef?.current;
    if (!el) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof (el as any).showPicker === 'function') (el as any).showPicker();
    else el.focus();
  };

  return (
    <div className={wrapperClass ?? 'w-full max-w-full'}>
      {label && (
        <label
          htmlFor={id}
          className={cn('block text-sm font-semibold mb-2', labelClass)}
        >
          {label}
        </label>
      )}

      <div className="relative">
        <button
          type="button"
          className="absolute inset-y-0 left-0 w-10 grid place-items-center cursor-pointer select-none"
          onMouseDown={e => {
            e.preventDefault();
            openPicker();
          }}
          aria-label="Open date picker"
        >
          <Calendar
            size={16}
            className={theme === 'dark' ? 'text-zinc-400' : 'text-zinc-500'}
          />
        </button>

        <input
          ref={el => {
            // Set both the forwarded ref and the internal ref
            if (typeof ref === 'function') {
              ref(el);
            } else if (ref) {
              (ref as React.MutableRefObject<HTMLInputElement | null>).current =
                el;
            }
            if (internalRef) {
              internalRef.current = el;
            }
          }}
          id={id}
          name={name}
          type="date"
          value={value ?? ''}
          min={min}
          max={max}
          placeholder={placeholder}
          onChange={e => setValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={cn(inputClass, 'hide-native-date')}
          disabled={disabled}
          aria-invalid={!!error || undefined}
          aria-describedby={error ? `${id}-error` : undefined}
        />
      </div>

      {withShortcuts && (
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
          <button
            type="button"
            className="underline hover:no-underline"
            onClick={setToday}
            disabled={disabled}
          >
            Today
          </button>
          <span className="opacity-50">•</span>
          <button
            type="button"
            className="underline hover:no-underline"
            onClick={() => setValue('')}
            disabled={disabled}
          >
            Clear
          </button>
          {/* hotkey hint hidden on very small screens */}
          <span className="ml-auto hidden sm:inline text-[10px] opacity-60">
            ↑/↓ day · ⌥ month · ⇧ year
          </span>
        </div>
      )}

      {error && (
        <p
          id={`${id}-error`}
          className="text-sm mt-1"
          style={{ color: getColour('danger') }}
        >
          {error}
        </p>
      )}

      <style>{`
          /* Hide native calendar icon but keep its hit area (iOS/Chrome) */
          .hide-native-date::-webkit-calendar-picker-indicator {
            opacity: 0;
            position: absolute;
            left: 0;
            width: 2.5rem; /* match our icon button width */
            height: 100%;
            cursor: pointer;
          }
          /* Remove spinner styles on some Android/Chrome variants */
          .hide-native-date::-webkit-inner-spin-button,
          .hide-native-date::-webkit-clear-button {
            display: none;
          }
          /* Ensure date value doesn't overflow on tiny screens */
          .hide-native-date::-webkit-datetime-edit-fields-wrapper {
            display: flex;
          }
          .hide-native-date::-webkit-datetime-edit {
            padding: 0; margin: 0;
          }
          .hide-native-date::-webkit-datetime-edit-year-field,
          .hide-native-date::-webkit-datetime-edit-month-field,
          .hide-native-date::-webkit-datetime-edit-day-field {
            padding: 0 1px;
          }
        `}</style>
    </div>
  );
});

DateInput.displayName = 'DateInput';
export default DateInput;
